/**
 * IPC for the multi-study viewer. Two transports under one API:
 *
 *  - **Local bus** (in-process EventTarget) — used when the modal viewer is
 *    rendered over the worklist in the same tab. BroadcastChannel does NOT
 *    deliver messages to its own context, so cross-tab plumbing alone misses
 *    the modal case; the local bus fills the gap with zero round-trip.
 *
 *  - **BroadcastChannel** — used when the viewer is in a separate tab
 *    ('multi-tab' mode). Cross-origin same-window only.
 *
 * `sendAddStudy` tries local first (synchronous ack flag), falls through to
 * BroadcastChannel + ack-with-timeout if no local listener handled it.
 *
 * `subscribeToAddStudy` registers on BOTH transports so a viewer in either
 * mode receives the request from anywhere.
 */
const CHANNEL_NAME = 'edgeweb-viewer';
const LOCAL_EVENT = 'edgeweb-add-study-local';

type AddStudyMessage = {
  type: 'add-study';
  studyId: number;
  /** Optional: series id to load into the active viewport when the study
   *  finishes loading. */
  seriesId?: number;
  nonce: string;
};
type AckMessage = { type: 'ack-study'; nonce: string };
type Message = AddStudyMessage | AckMessage;

let chan: BroadcastChannel | null = null;
function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!chan) chan = new BroadcastChannel(CHANNEL_NAME);
  return chan;
}

/** Singleton in-process bus. Modal viewer subscribes here; worklist
 *  dispatches here before falling through to the cross-tab channel. */
const localBus = new EventTarget();

interface LocalAddDetail {
  studyId: number;
  seriesId?: number;
  /** Listeners flip this true to indicate they handled the request. */
  ack: { fired: boolean };
}

/**
 * Try to push a study to a viewer. Local listeners (modal mode) are tried
 * first synchronously; if none acked, a BroadcastChannel post is sent and we
 * await an ack within `timeoutMs`.
 *
 * Resolves true if anyone (local OR cross-tab) acked, false otherwise — the
 * caller can then fall back to opening a fresh tab.
 */
export function sendAddStudy(
  studyId: number,
  seriesId?: number,
  timeoutMs = 250
): Promise<boolean> {
  // 1. Local dispatch — same-tab modal viewer
  const ack = { fired: false };
  const localEvt = new CustomEvent<LocalAddDetail>(LOCAL_EVENT, {
    detail: { studyId, seriesId, ack },
  });
  localBus.dispatchEvent(localEvt);
  if (ack.fired) return Promise.resolve(true);

  // 2. Cross-tab dispatch — separate viewer tab
  const ch = getChannel();
  if (!ch) return Promise.resolve(false);

  return new Promise((resolve) => {
    const nonce = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    let resolved = false;

    function onMessage(e: MessageEvent<Message>) {
      const m = e.data;
      if (m && m.type === 'ack-study' && m.nonce === nonce && !resolved) {
        resolved = true;
        ch!.removeEventListener('message', onMessage);
        resolve(true);
      }
    }
    ch.addEventListener('message', onMessage);
    ch.postMessage({ type: 'add-study', studyId, seriesId, nonce } as AddStudyMessage);

    setTimeout(() => {
      if (resolved) return;
      ch!.removeEventListener('message', onMessage);
      resolve(false);
    }, timeoutMs);
  });
}

/**
 * Viewer-side subscription. Listens on BOTH local bus (modal) and
 * BroadcastChannel (separate tab). Returns a single cleanup that removes
 * both listeners.
 */
export function subscribeToAddStudy(
  onAdd: (studyId: number, seriesId?: number) => void | Promise<void>
): () => void {
  function localHandler(e: Event) {
    const detail = (e as CustomEvent<LocalAddDetail>).detail;
    // Acknowledge synchronously so sendAddStudy can short-circuit the
    // BroadcastChannel attempt. The actual fetch is async; we don't need
    // to wait for it before signalling "yes, a viewer handled this".
    detail.ack.fired = true;
    Promise.resolve(onAdd(detail.studyId, detail.seriesId)).catch(() => {});
  }
  localBus.addEventListener(LOCAL_EVENT, localHandler);

  const ch = getChannel();
  let removeChannel = () => {};
  if (ch) {
    async function onMessage(e: MessageEvent<Message>) {
      const m = e.data;
      if (!m || m.type !== 'add-study') return;
      try { await onAdd(m.studyId, m.seriesId); } catch { /* swallow */ }
      ch!.postMessage({ type: 'ack-study', nonce: m.nonce } as AckMessage);
      try { window.focus(); } catch {}
    }
    ch.addEventListener('message', onMessage);
    removeChannel = () => ch.removeEventListener('message', onMessage);
  }

  return () => {
    localBus.removeEventListener(LOCAL_EVENT, localHandler);
    removeChannel();
  };
}
